import React, { CSSProperties, ReactElement } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Chevron } from '../icons/Chevron'
import { RADOM_COLORS } from '../util/Constants'
import { InputLabel } from './Input'

export const DropdownContext = React.createContext({
  closeDropdown: () => { }
})

const DropdownWrapper = styled.div<{ disabled?: boolean }>`
  user-select: none;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  position: relative;
`

const DropdownInner = styled.div<{ open: boolean, transparent?: boolean, disabled?: boolean, boxColor?: string }>`
  border: 1px solid;
  padding: 8px 12px;
  border-radius: 10px;
  transition: 0.2s ease all;
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 10px;
  justify-content: space-between;
  height: 100%;
  
  background-color: ${({ transparent, boxColor = 'white' }) => transparent ? 'transparent' : boxColor};
  border-color: ${({ open, transparent }) => (transparent ? 'transparent' : (open ? RADOM_COLORS.BLUE5 : '#d3dae8'))};
  
  ${({ open, transparent, disabled }) => open
    ? 'box-shadow: 0 0 0 3px rgb(97 138 250 / 18%);'
    : !transparent && !disabled && 'box-shadow: 0 1px 2px rgb(15 23 42 / 4%);'}

  :hover {
    background-color: ${({ transparent }) => transparent ? 'transparent' : 'white'};
    ${({ open, transparent, disabled }) => !open && !transparent && !disabled && 'border-color: #c4ccde;'}
  }

  :active {
    opacity: 1;
  }
`

const DropdownContent = styled.div<{ visible: boolean }>`
  cursor: auto;
  position: fixed;
  z-index: 99;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
  transform: ${({ visible }) => visible ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.985)'};
  transform-origin: 50% 0;
  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    visibility 0.18s ease;
`

const DropdownContentInner = styled.div<{ overflow?: string }>`
  max-height: 40vh;
  transform-origin: 50% 0;
  background-color: white;
  box-shadow: 0 20px 28px rgba(15, 23, 42, 0.12);
  border: 1px solid #dbe2ef;
  border-radius: 10px;
  ${({ overflow }) => overflow ? `overflow: ${overflow}` : 'overflow: auto'}
`

export const DropdownItem = styled.div`
  cursor: pointer;
  padding: 10px;
  transition: 0.2s ease all;
  opacity: 1;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }

  :active {
    opacity: 0.5;
  }
`

export const DropdownLink = styled(Link)`
  cursor: pointer;
  padding: 10px;
  transition: 0.2s ease all;
  opacity: 1;
  font-size: 14px;
  width: 100%;
  display: block;
  text-decoration: none;
  color: black;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }

  :active {
    opacity: 0.5;
  }
`

export const DropdownContentHeader = styled.div`
  display: flex;
  width: 100%;
  min-height: 42px;
  line-height: 16px;
  align-items: center;
  justify-content: space-between;
`

export const DropdownContentBody = styled.div`
  overflow-y: auto;
`

export const DropdownTextInput = styled.input<{ value: string }>`
  background-color: transparent;
  width: 100%;
  border: none;
  outline: none;
  flex-grow: 1;
  padding: 10px 12px;
  font-size: 14px;
`

type AlignmentPreference = 'left' | 'right'

interface IProps {
  preferredAlignment?: AlignmentPreference
  selectedContent: React.ReactNode
  dropdownContent: React.ReactNode
  onClose?: () => any
  onCloseFn?: (fn: any) => any
  transparent?: boolean
  whiteChevron?: boolean
  noChevron?: boolean
  disabled?: boolean
  overflow?: string
  onClick?: () => void
  openOnFocus?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  outerStyle?: CSSProperties
  innerStyle?: CSSProperties
  containerStyle?: CSSProperties
  dropdownMaxHeight?: number
}

interface IState {
  open: boolean
  top: number
  left: number
  maxHeight: number
}

export default class Dropdown extends React.Component<IProps, IState> {
  dropdownWrapper: HTMLDivElement | null
  dropdownContent: HTMLDivElement
  dropdownResizeObserver: ResizeObserver | null = null
  skipNextInnerClickToggle = false
  state = {
    open: false,
    top: 0,
    left: 0,
    maxHeight: 320
  }

  componentDidUpdate = (prevProps: IProps, prevState: IState): void => {
    if (prevProps.dropdownContent !== this.props.dropdownContent) {
      this.repositionDropdown()
    }

    if (!prevState.open && this.state.open) {
      this.repositionDropdown()
    }
  }

  onMouseDown = (e: MouseEvent): void => {
    if (!this.dropdownWrapper || !this.state.open) {
      return
    }

    const isClickInDropdown = this.dropdownWrapper === e.target ||
    this.dropdownWrapper.contains(e.target as any) ||
    !document.contains(e.target as any)
    if (!isClickInDropdown) {
      this.toggleDropdown(false)
    }
  }

  componentDidMount = (): void => {
    window.addEventListener('mousedown', this.onMouseDown)

    if (this.props.onCloseFn) {
      this.props.onCloseFn(() => this.toggleDropdown(false))
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('mousedown', this.onMouseDown)
    document.removeEventListener('wheel', this.repositionDropdown)
    window.removeEventListener('resize', this.repositionDropdown)
    this.dropdownResizeObserver?.disconnect()
  }

  toggleDropdown = (open = !this.state.open): void => {
    if (this.props.disabled) {
      return
    }

    if (open) {
      document.addEventListener('wheel', this.repositionDropdown)
      window.addEventListener('resize', this.repositionDropdown)
    } else {
      document.removeEventListener('wheel', this.repositionDropdown)
      window.removeEventListener('resize', this.repositionDropdown)
    }

    if (this.state.open && !open && this.props.onClose) {
      this.props.onClose()
    }

    this.setState({ open }, () => {
      if (open) {
        this.props.onClick?.()
      }
    })
  }

  onInnerClick = (): void => {
    // For focus-open dropdowns (e.g. searchable selectors), a mouse click can first
    // open on focus and then immediately trigger a click toggle that closes it.
    // Skip the first click after focus-open, then allow click toggling.
    if (this.props.openOnFocus) {
      if (this.skipNextInnerClickToggle) {
        this.skipNextInnerClickToggle = false
        return
      }

      this.toggleDropdown(!this.state.open)
      return
    }

    this.toggleDropdown()
  }

  repositionDropdown = (): void => {
    if (!this.dropdownWrapper || !this.dropdownContent || !this.state.open) {
      return
    }

    const wrapper = this.dropdownWrapper.getBoundingClientRect()
    const rect = this.dropdownContent.getBoundingClientRect()
    const viewportPadding = 8

    const bottomAlign = wrapper.height + wrapper.top + 5
    const topAlign = wrapper.y - rect.height - 5
    const availableBelow = window.innerHeight - bottomAlign - viewportPadding
    const availableAbove = wrapper.top - viewportPadding - 8

    let top = bottomAlign
    let shouldOpenAbove = false
    if (bottomAlign + rect.height > window.innerHeight - viewportPadding && topAlign >= viewportPadding) {
      // Position dropdown above dropdownWrapper when there is better space above.
      top = topAlign
      shouldOpenAbove = true
    } else if (bottomAlign + rect.height > window.innerHeight - viewportPadding && topAlign < viewportPadding) {
      top = Math.max(viewportPadding, window.innerHeight - rect.height - viewportPadding)
      shouldOpenAbove = availableAbove > availableBelow
    }

    let left = wrapper.x
    if (left + rect.width > window.innerWidth - viewportPadding) {
      left = wrapper.x - rect.width + wrapper.width
    }
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - rect.width - viewportPadding))

    const availableHeight = Math.floor(shouldOpenAbove ? availableAbove : window.innerHeight - top - viewportPadding)
    const configuredMaxHeight = this.props.dropdownMaxHeight ?? 360
    const viewportMaxHeight = Math.floor(window.innerHeight * 0.55)

    const dynamicMaxHeight = Math.max(
      170,
      Math.min(availableHeight, configuredMaxHeight, viewportMaxHeight)
    )

    this.setState(prev => {
      if (prev.top === top && prev.left === left && prev.maxHeight === dynamicMaxHeight) {
        return null
      }

      return { top, left, maxHeight: dynamicMaxHeight }
    })
  }

  render(): React.ReactNode {
    return (
      <DropdownWrapper
        style={this.props.containerStyle}
        ref={dropdownWrapper => { if (dropdownWrapper) this.dropdownWrapper = dropdownWrapper }}
        disabled={this.props.disabled}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={this.state.open}
        tabIndex={this.props.disabled ? -1 : 0}
        onFocus={e => {
          if (
            this.props.openOnFocus &&
            e.currentTarget === e.target &&
            !this.state.open &&
            !this.props.disabled
          ) {
            this.skipNextInnerClickToggle = true
            this.toggleDropdown(true)
          }
        }}
        onKeyDown={e => {
          this.props.onKeyDown?.(e)
          if (e.defaultPrevented) {
            return
          }

          if (e.currentTarget !== e.target) {
            return
          }

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.toggleDropdown()
            return
          }

          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            this.toggleDropdown(true)
            return
          }

          if (e.key === 'Escape' && this.state.open) {
            e.preventDefault()
            this.toggleDropdown(false)
          }
        }}>
        <DropdownInner

          transparent={this.props.transparent}
          open={this.state.open}
          onClick={this.onInnerClick}
          disabled={this.props.disabled}
          boxColor={this.props.innerStyle?.backgroundColor}
          style={this.props.outerStyle}>

          {this.props.selectedContent}
          {
            !this.props.noChevron &&
            <Chevron fill={this.props.whiteChevron ? 'white' : 'black'} style={{
              opacity: 0.3,
              transition: '0.2s ease all',
              transform: this.state.open ? 'rotate(180deg)' : 'rotate(0deg)',
              minWidth: 15
            }} />
          }
        </DropdownInner>
        {
          this.state.open &&
          <DropdownContent
            style={{
              minWidth: this.dropdownWrapper?.clientWidth,
              top: this.state.top,
              left: this.state.left
            }}
            ref={r => {
              if (!r || r === this.dropdownContent) return
              this.dropdownResizeObserver?.disconnect()
              this.dropdownResizeObserver = new ResizeObserver(this.repositionDropdown)
              this.dropdownResizeObserver.observe(r)
              this.dropdownContent = r
              this.repositionDropdown()
            }}
            visible={this.state.open}
          >
            <DropdownContext.Provider value={{ closeDropdown: () => this.toggleDropdown(false) }}>
              <DropdownContentInner
                overflow={this.props.overflow}
                style={{
                  maxHeight: this.state.maxHeight,
                  ...this.props.innerStyle
                }}
              >
                {this.props.dropdownContent}
              </DropdownContentInner>
            </DropdownContext.Provider>
          </DropdownContent>
        }
      </DropdownWrapper>
    )
  }
}

type DropdownWithLabelProps = IProps & {
  label: string
}

export const DropdownWithLabel =
  ({ label, selectedContent, dropdownContent, onCloseFn }: DropdownWithLabelProps): ReactElement => {
    return <>
      <InputLabel>{label}</InputLabel>
      <Dropdown selectedContent={selectedContent} dropdownContent={dropdownContent} onCloseFn={onCloseFn} />
    </>
  }
